import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { BookMarked, Loader2, MailCheck } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

export default function ForgotPassword() {
    const { t } = useTranslation();
    const [email, setEmail] = useState("");
    const [busy, setBusy] = useState(false);
    const [sent, setSent] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            await api.post("/auth/password-reset/request", {
                email: email.trim().toLowerCase(),
            });
            setSent(true);
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        } finally {
            setBusy(false);
        }
    };

    return (
        <main className="min-h-[calc(100vh-73px)] flex items-center justify-center px-5 py-12 md:py-16 bg-[var(--bg)]">
            <section className="w-full max-w-md">
                <Link
                    to="/"
                    className="flex items-center justify-center gap-2 text-[var(--ink)] mb-8"
                >
                    <BookMarked size={18} strokeWidth={1.5} />
                    <span
                        className="font-display text-2xl tracking-tight"
                        style={{ fontVariationSettings: '"wght" 600, "wdth" 90, "opsz" 32' }}
                    >
                        {t("nav.brand")}
                    </span>
                </Link>

                {sent ? (
                    <div data-testid="forgot-thanks" className="text-center">
                        <MailCheck size={28} strokeWidth={1.5} className="text-[var(--accent)] mb-4 mx-auto" />
                        <h1
                            className="font-display text-3xl text-[var(--ink)] mb-3"
                            style={{ fontVariationSettings: '"wght" 600, "wdth" 90, "opsz" 32' }}
                        >
                            {t("forgot.sent_title")}
                        </h1>
                        <p className="text-[var(--ink-muted)] text-sm leading-relaxed mb-8">
                            {t("forgot.sent_lead")}
                        </p>
                        <Link
                            to="/acceder"
                            data-testid="forgot-back-login"
                            className="inline-flex items-center text-sm uppercase tracking-widest text-[var(--ink-muted)] hover:text-[var(--accent)]"
                        >
                            {t("forgot.back_to_login")}
                        </Link>
                    </div>
                ) : (
                    <>
                        <p className="text-xs uppercase tracking-[0.3em] text-[var(--accent)] mb-3 text-center">
                            {t("forgot.eyebrow")}
                        </p>
                        <h1
                            className="font-display text-4xl sm:text-5xl text-[var(--ink)] text-center mb-3 tracking-tight"
                            style={{ fontVariationSettings: '"wght" 600, "wdth" 85, "opsz" 96' }}
                        >
                            {t("forgot.title")}
                        </h1>
                        <p className="text-center text-sm text-[var(--ink-muted)] mb-10 max-w-xs mx-auto">
                            {t("forgot.lead")}
                        </p>
                        <form onSubmit={submit} data-testid="forgot-form" className="space-y-6">
                            <div>
                                <label className="block text-xs uppercase tracking-widest text-[var(--ink-muted)] mb-2">
                                    {t("auth.email")}
                                </label>
                                <input
                                    data-testid="forgot-email-input"
                                    type="email"
                                    required
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    className="w-full bg-transparent border-b border-[var(--ink)] py-2 text-lg text-[var(--ink)] focus:outline-none focus:border-[var(--accent)] transition-colors"
                                />
                            </div>
                            <button
                                data-testid="forgot-submit-btn"
                                type="submit"
                                disabled={busy}
                                className="w-full inline-flex items-center justify-center gap-2 bg-[var(--ink)] text-[var(--bg)] rounded-full hover:bg-[var(--accent)] disabled:opacity-50 transition-colors px-8 py-3.5 text-sm uppercase tracking-widest"
                            >
                                {busy && <Loader2 size={14} className="animate-spin" />}
                                {t("forgot.submit")}
                            </button>
                        </form>
                        <p className="text-center text-sm text-[var(--ink-muted)] mt-8">
                            <Link
                                to="/acceder"
                                className="text-[var(--ink)] underline underline-offset-4 hover:text-[var(--accent)]"
                            >
                                {t("forgot.back_to_login")}
                            </Link>
                        </p>
                    </>
                )}
            </section>
        </main>
    );
}
